import { useState } from 'react';
import { Stack, Title, Text, Button, ThemeIcon, TextInput } from '@mantine/core';
import { IconMail, IconArrowRight } from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { setContactEmail } from '../../services/contactEmailService';
import { ApiError } from '../../services/ApiError';

type ContactEmailStepProps = {
  value: any;
  onChange: (value: any) => void;
  onNext: () => void;
  onBack: () => void;
  isLastStep: boolean;
  isFirstStep?: boolean;
  isCompleting?: boolean;
};

/**
 * Shown when the signed-in account has no email address (Facebook can grant
 * sign-in without one). Submitting sends a confirmation link; the address
 * activates when that link is clicked, so we let the user continue onboarding
 * right away rather than holding them hostage to their inbox.
 */
export const ContactEmailStep = ({ onNext }: ContactEmailStepProps) => {
  const { user } = useAuth();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const validEmail = /^\S+@\S+$/.test(email.trim());

  const handleSubmit = async () => {
    if (!user || !validEmail) return;
    setSubmitting(true);
    setError(null);
    try {
      const token = await user.getIdToken();
      await setContactEmail(email.trim(), token);
      setSentTo(email.trim());
    } catch (err) {
      if (err instanceof ApiError && err.statusCode === 429) {
        setError('Too many attempts. Please wait a few minutes and try again.');
      } else {
        setError('Something went wrong sending the confirmation link. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      <Stack gap="md" ta="center" align="center">
        <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
          <IconMail size={40} />
        </ThemeIcon>
        <Stack gap="xs" align="center">
          <Title order={2} size="h1" fw={700} c="dark">
            Add your email
          </Title>
          {sentTo ? (
            <Text size="lg" c="dimmed" ta="center" maw={440}>
              We sent a confirmation link to <strong>{sentTo}</strong>. Click it
              whenever you get a chance. You can keep going in the meantime.
            </Text>
          ) : (
            <Text size="lg" c="dimmed" ta="center" maw={440}>
              Your account doesn't have an email address yet. Add one so we can
              send you your meal plans and account updates.
            </Text>
          )}
          {error && (
            <Text size="sm" c="red">
              {error}
            </Text>
          )}
        </Stack>
      </Stack>

      {sentTo ? (
        <Stack gap="md" align="center" pt="md">
          <Button
            size="md"
            color="orange"
            rightSection={<IconArrowRight size={18} />}
            onClick={onNext}
          >
            Continue
          </Button>
          <Button
            variant="subtle"
            size="xs"
            color="orange"
            onClick={() => {
              setSentTo(null);
              setEmail('');
            }}
          >
            Use a different address
          </Button>
        </Stack>
      ) : (
        <Stack gap="md" align="center" pt="md">
          <TextInput
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(event) => setEmail(event.currentTarget.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') handleSubmit();
            }}
            size="md"
            radius="md"
            w={{ base: '100%', sm: 340 }}
            aria-label="Email address"
          />
          <Button
            size="md"
            color="orange"
            rightSection={<IconArrowRight size={18} />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={!validEmail}
          >
            Send confirmation link
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
